import React, { useState, useEffect } from 'react';
import { FaCalculator, FaChartLine, FaExclamationTriangle } from 'react-icons/fa';
import useDailyPlan from '../../../hooks/useDailyPlan';

export default function DailyPlanPanel({ capital, onCapitalChange }) {
  const { plan, loading, generatePlan } = useDailyPlan();
  const [riskPct, setRiskPct] = useState(1.0);
  const [dailyGainPct, setDailyGainPct] = useState(2.0);
  const [localCapital, setLocalCapital] = useState(capital || 50000);

  useEffect(() => {
    if (capital) setLocalCapital(capital);
  }, [capital]);

  const handleGenerate = () => {
    generatePlan(localCapital, riskPct, dailyGainPct);
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <div className="flex items-center gap-2 mb-4">
        <FaCalculator className="text-blue-500" />
        <h3 className="text-gray-900 font-semibold text-sm">Daily Trade Plan</h3>
      </div>

      {/* Input fields */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div>
          <label className="text-[10px] text-gray-400 uppercase block mb-1">Capital</label>
          <input
            type="number"
            value={localCapital}
            onChange={e => {
              setLocalCapital(Number(e.target.value));
              onCapitalChange?.(Number(e.target.value));
            }}
            className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-gray-900 focus:border-blue-400 focus:ring-1 focus:ring-blue-400 outline-none"
          />
        </div>
        <div>
          <label className="text-[10px] text-gray-400 uppercase block mb-1">Risk (%)</label>
          <input
            type="number"
            step="0.5"
            value={riskPct}
            onChange={e => setRiskPct(Number(e.target.value))}
            className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-gray-900 focus:border-blue-400 focus:ring-1 focus:ring-blue-400 outline-none"
          />
        </div>
        <div>
          <label className="text-[10px] text-gray-400 uppercase block mb-1">Target (%)</label>
          <input
            type="number"
            step="0.5"
            value={dailyGainPct}
            onChange={e => setDailyGainPct(Number(e.target.value))}
            className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5 text-sm text-gray-900 focus:border-blue-400 focus:ring-1 focus:ring-blue-400 outline-none"
          />
        </div>
      </div>

      <button
        onClick={handleGenerate}
        disabled={loading}
        className="w-full bg-blue-600 text-white rounded-lg py-2 mb-4 text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
      >
        {loading ? 'Calculating...' : 'Generate Plan'}
      </button>

      {/* Plan display */}
      {plan && (
        <div className="space-y-3">
          {!plan.achievable && (
            <div className="p-2 rounded bg-amber-50 border border-amber-200 flex items-start gap-2">
              <FaExclamationTriangle className="text-amber-500 mt-0.5 text-xs flex-shrink-0" />
              <span className="text-xs text-amber-700">
                Target not achievable with current stats. Improve win rate or RR ratio.
              </span>
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            <PlanStat label="Risk per Trade" value={`₹${plan.risk_per_trade}`} />
            <PlanStat label="Daily Target" value={`₹${plan.daily_target}`} color="emerald" />
            <PlanStat label="Expected/Trade" value={`₹${plan.expected_gain_per_trade}`} color={plan.expected_gain_per_trade > 0 ? 'emerald' : 'red'} />
            <PlanStat label="Required Trades" value={plan.required_trades} />
            <PlanStat label="Avg Win Rate" value={`${(plan.avg_win_rate * 100).toFixed(0)}%`} />
            <PlanStat label="Avg RR" value={`${plan.avg_rr}:1`} />
          </div>

          <div className="p-2 rounded bg-gray-50 border border-gray-200">
            <div className="text-[10px] text-gray-400 uppercase mb-1">Position Sizing</div>
            <div className="text-xs text-gray-700 font-mono">{plan.position_sizing_formula}</div>
          </div>

          <div className="flex items-center gap-2 text-xs text-gray-400">
            <FaChartLine />
            <span>Max loss if all trades fail: <span className="text-red-600">₹{plan.max_loss_if_all_fail}</span></span>
          </div>
        </div>
      )}
    </div>
  );
}

function PlanStat({ label, value, color = 'gray' }) {
  const colorClass = {
    gray: 'text-gray-900',
    emerald: 'text-emerald-600',
    red: 'text-red-600',
    amber: 'text-amber-600',
  }[color] || 'text-gray-900';

  return (
    <div className="bg-gray-50 rounded-lg p-2">
      <div className="text-[10px] text-gray-400 uppercase">{label}</div>
      <div className={`text-sm font-semibold ${colorClass}`}>{value}</div>
    </div>
  );
}
